// EN-4743. Turning the live camera picture into an image the MRZ OCR can read.

export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The on-screen guide mapped into the video's own pixels.
 *
 * The <video> is shown with `object-fit: cover`, so it's scaled up and cropped to
 * fill its container; the guide's CSS rectangle has to be mapped back through that
 * scale and offset.
 */
export const guideInVideoPixels = (video: HTMLVideoElement, stageEl: Element, guideEl: Element): Region | null => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  const stage = stageEl.getBoundingClientRect();
  const guide = guideEl.getBoundingClientRect();
  const scale = Math.max(stage.width / vw, stage.height / vh);
  const offX = (stage.width - vw * scale) / 2;
  const offY = (stage.height - vh * scale) / 2;
  const x = Math.max(0, (guide.left - stage.left - offX) / scale);
  const y = Math.max(0, (guide.top - stage.top - offY) / scale);
  return { x, y, w: Math.min(guide.width / scale, vw - x), h: Math.min(guide.height / scale, vh - y) };
};

// Each OCR pass uses the next crop in this cycle. `band` is the share of the guide
// height taken from its bottom; `width` is what the crop is scaled to. Varying
// both makes two passes fail in different ways, which is what makes the two-read
// agreement (./mrzReadAgreement.ts) meaningful.
export const MRZ_VARIANTS = [
  { band: 0.25, width: 1400 },
  { band: 0.35, width: 1800 },
  { band: 0.25, width: 1800 },
  { band: 0.35, width: 1400 },
] as const;

// Adaptive threshold settings. The window is about 1.5 characters wide; C is how
// much darker than its neighbourhood a pixel must be to count as ink. A single
// global threshold failed on a photo lit from one side: the darker third of both
// MRZ lines was lost.
const ADAPTIVE_WINDOW_DIVISOR = 28;
const ADAPTIVE_C = 12;

/**
 * Crop the bottom band of `region`, scale it to `targetWidth`, and convert it to
 * black and white with a local-mean adaptive threshold. Draws into `canvas` and
 * returns it.
 */
export const prepareMrzCrop = (
  source: CanvasImageSource,
  region: Region,
  bandFraction: number,
  targetWidth: number,
  canvas: HTMLCanvasElement,
): HTMLCanvasElement => {
  const bandH = region.h * bandFraction;
  const sy = region.y + region.h - bandH;
  const scale = targetWidth / region.w;
  const w = (canvas.width = Math.round(region.w * scale));
  const h = (canvas.height = Math.round(bandH * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return canvas;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, region.x, sy, region.w, bandH, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;

  const gray = new Uint8ClampedArray(w * h);
  for (let i = 0, p = 0; p < gray.length; i += 4, p += 1) {
    gray[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  }
  // Integral image, so each pixel's local mean costs four lookups.
  const W = w + 1;
  const integral = new Float64Array(W * (h + 1));
  for (let y = 0; y < h; y += 1) {
    let row = 0;
    for (let x = 0; x < w; x += 1) {
      row += gray[y * w + x];
      integral[(y + 1) * W + x + 1] = integral[y * W + x + 1] + row;
    }
  }
  const r = (Math.round(w / ADAPTIVE_WINDOW_DIVISOR) | 1) >> 1;
  for (let y = 0; y < h; y += 1) {
    const y0 = Math.max(0, y - r);
    const y1 = Math.min(h, y + r + 1);
    for (let x = 0; x < w; x += 1) {
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w, x + r + 1);
      const sum = integral[y1 * W + x1] - integral[y0 * W + x1] - integral[y1 * W + x0] + integral[y0 * W + x0];
      const v = gray[y * w + x] < sum / ((x1 - x0) * (y1 - y0)) - ADAPTIVE_C ? 0 : 255;
      const i = (y * w + x) * 4;
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
};
